import { Component, inject, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CartValidationResult, UploadResilienceService } from '../../../core/services/upload-resilience.service';
import { AlertService } from '../../../core/services/alert.service';
import { filterOversizedFiles, filterEditorImageFiles, EDITOR_IMAGE_ACCEPT } from '../../../core/models/photo.model';

export interface CartBlockedDialogData extends CartValidationResult {
  /** All project IDs for re-validation */
  projectIds?: number[];
}

@Component({
  selector: 'app-cart-photos-blocked-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, MatProgressBarModule, MatProgressSpinnerModule, TranslateModule],
  template: `
    <div class="blocked-dialog">
      <div class="blocked-header">
        <div class="blocked-icon">
          <mat-icon>{{ data.reason === 'photos_missing' ? 'photo_library' : (data.reason === 'validation_failed' ? 'error_outline' : 'cloud_upload') }}</mat-icon>
        </div>
        <h2>{{ 'CART_BLOCKED.TITLE' | translate }}</h2>
        <p class="blocked-subtitle">{{ 'CART_BLOCKED.SUBTITLE' | translate }}</p>
      </div>

      <div class="blocked-body">
        @if (data.reason === 'uploads_in_progress') {
          <div class="blocked-detail">
            <mat-icon class="detail-icon uploading">cloud_upload</mat-icon>
            <div class="detail-text">
              <strong>{{ 'CART_BLOCKED.UPLOADS_IN_PROGRESS' | translate }}</strong>
              <span class="detail-sub">
                {{ uploadResilience.completedCount() }}/{{ uploadResilience.totalCount() }}
                {{ 'UPLOAD_TOAST.PHOTOS' | translate }}
              </span>
            </div>
          </div>
          <mat-progress-bar mode="determinate" [value]="uploadResilience.uploadProgress()"></mat-progress-bar>
        } @else if (data.reason === 'reuploads_in_progress') {
          <div class="blocked-detail">
            <mat-icon class="detail-icon uploading">cloud_sync</mat-icon>
            <div class="detail-text">
              <strong>{{ 'CART_BLOCKED.REUPLOADS_IN_PROGRESS' | translate }}</strong>
              <span class="detail-sub">
                {{ uploadResilience.reuploadResolvedCount() }}/{{ uploadResilience.reuploadTotalCount() }}
                {{ 'UPLOAD_TOAST.PHOTOS' | translate }}
              </span>
            </div>
          </div>
          <mat-progress-bar mode="determinate" [value]="uploadResilience.reuploadProgress()"></mat-progress-bar>
        } @else if (data.reason === 'photos_missing') {
          @if (photoStates().length > 0) {
            <div class="blocked-detail">
              <mat-icon class="detail-icon missing">warning</mat-icon>
              <div class="detail-text">
                <strong>{{ 'CART_BLOCKED.PHOTOS_MISSING' | translate: { count: photoStates().length } }}</strong>
                <span class="detail-sub">{{ 'CART_BLOCKED.PHOTOS_MISSING_SUB' | translate }}</span>
              </div>
            </div>

            <!-- Photo grid showing missing photos with per-photo upload status -->
            <div class="missing-grid">
              @for (state of photoStates(); track state.photo.md5) {
                <div class="missing-card" [class.is-uploading]="state.status === 'uploading'">
                  <div class="missing-thumb">
                    @if (state.status === 'uploading') {
                      <mat-spinner diameter="24" strokeWidth="2"></mat-spinner>
                    } @else if (state.photo.thumbnail) {
                      <img [src]="state.photo.thumbnail" [alt]="state.photo.name">
                    } @else {
                      <mat-icon>image</mat-icon>
                    }
                  </div>
                  <div class="missing-name" [title]="state.photo.name">{{ state.photo.name }}</div>
                </div>
              }
            </div>

            <!-- Upload button -->
            <button mat-raised-button class="upload-btn" (click)="triggerFileInput()"
                    [disabled]="uploadResilience.isReuploadProcessing()">
              @if (uploadResilience.isReuploadProcessing()) {
                <mat-spinner diameter="16" strokeWidth="2"></mat-spinner>
              } @else {
                <mat-icon>file_upload</mat-icon>
              }
              {{ 'REUPLOAD.LOCATE_FILES' | translate }}
            </button>
          } @else {
            <!-- Every reported photo has been re-uploaded in this session —
                 never render the absurd "0 photo(s) missing" with a dead
                 Locate button. -->
            <div class="blocked-detail">
              <mat-icon class="detail-icon resolved">check_circle</mat-icon>
              <div class="detail-text">
                <strong>{{ 'REUPLOAD.FILES_RESOLVED_TITLE' | translate }}</strong>
                <span class="detail-sub">{{ 'CART_BLOCKED.ALL_LOCATED_RETRY' | translate }}</span>
              </div>
            </div>
          }
        } @else if (data.reason === 'validation_failed') {
          <div class="blocked-detail">
            <mat-icon class="detail-icon missing">error_outline</mat-icon>
            <div class="detail-text">
              <strong>{{ 'CART_BLOCKED.VALIDATION_FAILED' | translate }}</strong>
              <span class="detail-sub">{{ 'CART_BLOCKED.VALIDATION_FAILED_SUB' | translate }}</span>
            </div>
          </div>
        }
      </div>

      <div class="blocked-actions">
        <button mat-stroked-button (click)="close()">{{ 'REUPLOAD.CLOSE' | translate }}</button>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .blocked-dialog { padding: 24px; text-align: center; box-sizing: border-box; }
    .blocked-header { margin-bottom: 16px; }
    .blocked-icon {
      width: 52px; height: 52px; border-radius: 13px;
      background: rgba(239, 96, 49, 0.08);
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 12px;
      mat-icon { font-size: 24px; width: 24px; height: 24px; color: #EF6031; }
    }
    h2 { margin: 0 0 4px; font-size: 16px; font-weight: 600; color: #2c2c2c; }
    .blocked-subtitle { margin: 0; font-size: 12px; color: #999; }
    .blocked-body { margin-bottom: 18px; }
    .blocked-detail {
      display: flex; align-items: center; gap: 10px; text-align: left;
      padding: 10px 14px; background: #f8f8f6; border-radius: 8px; margin-bottom: 10px;
    }
    .detail-icon { font-size: 20px; width: 20px; height: 20px; flex-shrink: 0; }
    .detail-icon.uploading { color: #3c7056; }
    .detail-icon.missing { color: #EF6031; }
    .detail-icon.resolved { color: #3c7056; }
    .detail-text { display: flex; flex-direction: column; gap: 1px; }
    .detail-text strong { font-size: 13px; color: #333; }
    .detail-text .detail-sub { font-size: 11px; color: #999; }
    mat-progress-bar {
      height: 4px; border-radius: 2px; margin-bottom: 10px;
      ::ng-deep .mdc-linear-progress__bar-inner { border-color: #3c7056; }
      ::ng-deep .mdc-linear-progress__buffer-bar { background-color: rgba(60, 112, 86, 0.12); }
    }
    .missing-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
      gap: 8px; max-height: 220px; overflow-y: auto; margin-bottom: 12px;
      padding: 4px;
    }
    .missing-card {
      border: 1px solid #e8e4df; border-radius: 6px; overflow: hidden;
      background: #fafaf8;
      &.is-uploading {
        border-color: #3c7056; background: rgba(60, 112, 86, 0.04);
        .missing-thumb { background: rgba(60, 112, 86, 0.08); }
      }
    }
    .missing-thumb {
      width: 100%; aspect-ratio: 1; display: flex; align-items: center;
      justify-content: center; background: #f0eeeb; overflow: hidden;
      img { width: 100%; height: 100%; object-fit: cover; }
      mat-icon { font-size: 28px; width: 28px; height: 28px; color: #ccc; }
    }
    .missing-name {
      font-size: 10px; color: #666; padding: 4px 6px;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .upload-btn {
      width: 100%; border-radius: 8px !important;
      background: #3c7056 !important; color: white !important;
      display: flex; align-items: center; justify-content: center; gap: 6px;
      mat-icon { font-size: 18px; width: 18px; height: 18px; }
    }
    .blocked-actions {
      display: flex; justify-content: center; gap: 8px;
      button { border-radius: 8px; padding: 6px 20px; }
    }
  `]
})
export class CartPhotosBlockedDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<CartPhotosBlockedDialogComponent>);
  readonly data: CartBlockedDialogData = inject(MAT_DIALOG_DATA);
  readonly uploadResilience = inject(UploadResilienceService);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    // This dialog opens with a FRESH server validation: any md5 it reports
    // missing is missing NOW, even if an earlier re-upload round marked it
    // resolved in this session (the re-upload restores the storage file but
    // cannot rewrite the saved project JSON, so the next validation can
    // legitimately re-flag the same photos). Without this reset the stale
    // states filter every entry out and the dialog renders "0 photo(s)
    // missing" with an empty grid and a Locate button that does nothing.
    if (this.data.reason === 'photos_missing' && this.data.missingPhotos?.length) {
      this.uploadResilience.resetReuploadStatesFor(this.data.missingPhotos.map(p => p.md5));
    }
  }

  // Merge missing photos with real-time upload states (like editor's reupload dialog)
  photoStates = computed(() => {
    const states = this.uploadResilience.reuploadStates();
    return (this.data.missingPhotos || [])
      .map(p => {
        const state = states.get(p.md5);
        return {
          photo: p,
          status: (state?.status || 'missing') as 'missing' | 'uploading' | 'resolved' | 'error'
        };
      })
      .filter(s => s.status !== 'resolved');
  });

  close(): void {
    this.dialogRef.close();
  }

  triggerFileInput(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = EDITOR_IMAGE_ACCEPT;
    input.onchange = () => {
      if (input.files?.length) {
        const files = Array.from(input.files);
        const { validFiles, oversizedNames } = filterOversizedFiles(files);
        if (oversizedNames.length > 0) {
          this.alertService.warning(
            this.translate.instant('PRODUCTS.VALIDATION.FILE_TOO_LARGE', { files: oversizedNames.join(', ') })
          );
        }
        const { validFiles: formatValidFiles, rejectedNames } = filterEditorImageFiles(validFiles);
        if (rejectedNames.length > 0) {
          this.alertService.warning(
            this.translate.instant('EDITOR.FORMAT_NOT_ALLOWED', { files: rejectedNames.join(', ') })
          );
        }
        if (formatValidFiles.length > 0) {
          const stillMissing = this.photoStates()
            .filter(s => s.status === 'missing')
            .map(s => s.photo);
          if (stillMissing.length > 0) {
            // Group by projectId so each upload goes to the correct project endpoint
            const byProject = new Map<number, typeof stillMissing>();
            for (const p of stillMissing) {
              const arr = byProject.get(p.projectId) ?? [];
              arr.push(p);
              byProject.set(p.projectId, arr);
            }
            // Queue per project (processReuploadQueue skips non-matching MD5s)
            byProject.forEach((photos, projectId) => {
              this.uploadResilience.queueReupload(formatValidFiles, projectId, photos, () => {});
            });
          }
        }
      }
    };
    input.click();
  }
}
